import {useEffect, useRef, useState, type CSSProperties} from 'react'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useDiffEntries,
  useDiffEntriesCount,
  useDiffEntriesWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useProgressiveDiffActions,
} from '../stores/selectors'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {useWindowVirtualizer} from '@tanstack/react-virtual'
import {PullRequestDiff} from './PullRequestDiff'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {DiffEntryLoadingSkeleton} from './progressive-diff/DiffEntryLoadingSkeleton'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'

export interface PullRequestVirtualizedDiffsListProps {
  basePath: string
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  basePath,
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  'use no memo'

  const [diffManuallyExpanded, __] = useState(false)
  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const progressiveDiffEntries = useDiffEntriesWithFilter(filteredDiffSummaries)
  const activeChangeGroup = useActiveChangeGroup()

  const {selectedPathDigest} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const {loadMore: loadMoreDiffEntries} = useProgressiveDiffActions()
  const diffEntries = useDiffEntries()
  const diffEntriesCount = useDiffEntriesCount()

  const totalVisibleEntries = progressiveDiffEntries.length
  const shouldShowWhimsy = totalVisibleEntries >= 15

  const estimateVirtualRowSize = (index: number) => {
    const progressiveDiffEntry = progressiveDiffEntries[index]
    if (!progressiveDiffEntry) {
      return 0
    }

    const diffSummary = allDiffSummaries[progressiveDiffEntry.path]

    let approximateLineCount = 5
    if (diffSummary?.linesChanged) {
      approximateLineCount = diffSummary.linesChanged
    }
    const heightPerLine = currentUser.lineSpacing === 'compact' ? 20 : 25
    const height = heightPerLine * approximateLineCount

    return height + 75
  }
  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const virtualizer = useWindowVirtualizer({
    count: progressiveDiffEntries.length,
    estimateSize: estimateVirtualRowSize,
    overscan: 3,
    scrollMargin,
    gap: 16,
  })

  const virtualItems = virtualizer.getVirtualItems()

  useEffect(() => {
    const lastItem = virtualItems.at(-1)

    if (!lastItem) {
      return
    }

    let idleCallback: number | undefined

    if (lastItem.index >= diffEntriesCount - 1) {
      const progressiveDiffEntry = progressiveDiffEntries[lastItem.index]
      if (progressiveDiffEntry) {
        // Temporarily using a timeout solution here to avoid issues when scrolling fast.
        idleCallback = requestIdleCallback(() => loadMoreDiffEntries(progressiveDiffEntry), {timeout: 100})
      }
    }

    // Component effect cleanup if there is an idleCallback set
    if (idleCallback) {
      return () => cancelIdleCallback(idleCallback)
    }
  }, [virtualItems, progressiveDiffEntries, loadMoreDiffEntries, diffEntriesCount])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffSummaries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map(virtualRow => {
          const progressiveDiffEntry = progressiveDiffEntries[virtualRow.index]

          if (!progressiveDiffEntry) return null

          const isLastItem = virtualRow.index === progressiveDiffEntries.length - 1
          const virtualRowStyle: CSSProperties = {
            position: 'absolute',
            top: 0,
            left: 0,
            transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
            marginBottom: isLastItem ? 0 : '16px',
          }

          if (progressiveDiffEntry.loadingStatus === 'NotLoaded') {
            const diffSummary = allDiffSummaries[progressiveDiffEntry.path]

            let approximateLineCount = 5

            if (diffSummary?.linesChanged) {
              approximateLineCount = diffSummary.linesChanged
            }

            if (diffSummary?.changeType === 'REMOVED' || diffSummary?.changeType === 'DELETED') {
              approximateLineCount = 5
            }
            return (
              <div
                key={virtualRow.key}
                style={virtualRowStyle}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
              >
                <DiffEntryLoadingSkeleton
                  ariaLabel={`Loading ${progressiveDiffEntry.path}`}
                  testId={`lazy-load-${progressiveDiffEntry.path}`}
                  id={`diff-${progressiveDiffEntry.pathDigest}`}
                  approximateLineCount={approximateLineCount}
                />
              </div>
            )
          }

          const diffEntry = diffEntries[progressiveDiffEntry.path]

          if (!diffEntry) return null

          return (
            <div
              key={virtualRow.key}
              style={virtualRowStyle}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
            >
              <PullRequestDiff
                basePath={basePath}
                changeType={diffEntry.status}
                currentUser={currentUser}
                diffLines={diffEntry.diffLines}
                diffManuallyExpanded={diffManuallyExpanded}
                diffSize={diffEntry.diffSize}
                headerStickyOffset={-(virtualRow.start - virtualizer.options.scrollMargin)}
                headBranchName={headBranchName}
                headRepoName={headRepoName}
                headRepoOwner={headRepoOwner}
                helpUrl={diffEntry.helpUrl}
                isBinary={diffEntry.isBinary}
                isSelected={selectedPathDigest === undefined ? undefined : diffEntry.pathDigest === selectedPathDigest}
                isSubmodule={diffEntry.isSubmodule}
                isTooBig={diffEntry.isTooBig}
                linesAdded={diffEntry.linesAdded}
                linesChanged={diffEntry.linesChanged}
                linesDeleted={diffEntry.linesDeleted}
                newCommitOid={diffEntry.newCommitOid}
                newTreeEntry={diffEntry.newTreeEntry}
                objectId={diffEntry.objectId}
                oldCommitOid={diffEntry.oldCommitOid}
                oldTreeEntry={diffEntry.oldTreeEntry}
                path={diffEntry.path}
                pathDigest={diffEntry.pathDigest}
                pullRequestGlobalRelayId={pullRequestGlobalRelayId}
                repository={repository}
                reviewed={diffEntry.reviewed ?? false}
                richDiff={diffEntry.richDiff}
                status={diffEntry.status}
                submodule={diffEntry.submodule}
                truncatedReason={diffEntry.truncatedReason}
              />
            </div>
          )
        })}
      </div>
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

try{ PullRequestVirtualizedDiffsList.displayName ||= 'PullRequestVirtualizedDiffsList' } catch {}